// FilesModal.tsx — staged-files popup for the Add New Product (V6) screen.
// Files upload user-scoped ("global") through the existing vector-file
// pipeline and are linked to the product at create time via
// attachStagedArtifacts. Spec:
// docs/superpowers/specs/2026-07-17-newproduct-files-staging-design.md
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { NPGlyph, NPIcon } from "./atoms";
import type { SourceConnection } from "./state";
import { cleanupStagedArtifacts } from "@/api/stagedArtifacts";
import {
    UploadQueueList,
    useSequentialFileUploadQueue,
    type SequentialFileUploadQueueLifecycleHandlers
} from "@/components/fileUploadQueue";
import type { UploadQueueRow } from "@/components/fileUploadQueue";
import useFileUpload, { type FileExtractionEvent } from "@/hooks/useFileUpload";
import { useProgrammaticIntakeQueue } from "../useProgrammaticIntakeQueue";

const ACCEPTED_FILE_TYPES = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.html,.json,.md";

export function FilesModal({ existing, token, onFileAdded, onFileRemoved, onClose, intakeFiles, onIntakeConsumed, onImportProgressChanged }: {
    existing: SourceConnection;
    token: string;
    onFileAdded: (fileId: number, fileName?: string) => void;
    onFileRemoved: (fileId: number) => void;
    onClose: () => void;
    /** Files handed to the screen programmatically (FDE chat drops). Fed one at
     *  a time through the same upload modal as a manual pick. */
    intakeFiles?: File[];
    onIntakeConsumed?: () => void;
    /** Publishes upload/extraction/indexing progress for FDE status answers. */
    onImportProgressChanged?: (rows: UploadQueueRow[]) => void;
}) {
    const [removingId, setRemovingId] = useState<number | null>(null);
    const lifecycleHandlersRef = useRef<SequentialFileUploadQueueLifecycleHandlers | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const lastIntakeTokenRef = useRef<string | null>(null);

    const { handleUpload } = useFileUpload({
        onUploadProgress: progress => lifecycleHandlersRef.current?.onUploadProgress(progress),
        onSavingFileToDatabaseStart: () => lifecycleHandlersRef.current?.onSavingFileToDatabaseStart(),
        onFileTextExtractionEvent: (_fileName: string, event: FileExtractionEvent) => {
            lifecycleHandlersRef.current?.onFileTextExtractionEvent(event);
        },
        onError: message => {
            lifecycleHandlersRef.current?.onUploadError(message);
        }
    });

    const queue = useSequentialFileUploadQueue({
        handleUpload,
        onFileSucceeded: ({ fileId, file }) => onFileAdded(fileId, file.name),
        onFileFailed: ({ error }) => {
            if (/\b401\b/.test(error)) toast.error("Session expired - please retry.");
            else toast.error(error || "Upload failed");
        },
        registerLifecycleHandlers: handlers => {
            lifecycleHandlersRef.current = handlers;
        }
    });

    useEffect(() => {
        onImportProgressChanged?.(queue.rows);
    }, [onImportProgressChanged, queue.rows]);

    // Chain intake files through the single-file upload modal: pull the next
    // one whenever no upload is in flight. onIntakeConsumed pops the head.
    useProgrammaticIntakeQueue({
        intakeFiles,
        onIntakeConsumed,
        onConsumeFile: file => queue.enqueueFiles([file])
    });

    const fileIds = existing.staged?.fileIds ?? [];
    const nameFor = (id: number) =>
        existing.selection?.find(item => item.id === String(id))?.name ?? `File #${id}`;

    const handlePick = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files ? Array.from(event.target.files) : [];
        event.target.value = "";
        queue.enqueueFiles(files);
    };

    const handleRemove = async (id: number) => {
        setRemovingId(id);
        try {
            await cleanupStagedArtifacts({ fileIds: [id] }, token);
            onFileRemoved(id);
        } catch (error) {
            console.error("Failed to remove staged file", error);
            toast.error(`Could not remove ${nameFor(id)} — try again.`);
        } finally {
            setRemovingId(null);
        }
    };

    const uploadRows = queue.rows.filter(row => row.stage !== "indexed");
    const recentlyIndexedRows = queue.rows.filter(row => row.stage === "indexed").slice(-3);

    return (
        <div
            style={{
                position: "fixed", inset: 0, zIndex: 60, background: "rgba(15,23,42,0.42)",
                display: "flex", alignItems: "center", justifyContent: "center"
            }}
            onClick={onClose}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    width: 470, maxHeight: "86%", overflow: "hidden auto",
                    background: "var(--bg-paper)", borderRadius: 16,
                    boxShadow: "0 24px 70px rgba(15,23,42,0.35)", padding: "20px 24px 22px"
                }}
            >
                {/* header — mirrors ConnectSourceModal */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                    <span style={{
                        width: 38, height: 38, borderRadius: 11, border: "1px solid var(--divider)",
                        display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto"
                    }}>
                        <NPGlyph name="files" size={20} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 650 }}>Files</div>
                        <div style={{ fontSize: 12, color: "var(--fg3)" }}>
                            {fileIds.length === 0
                                ? "No files staged"
                                : `${fileIds.length} file${fileIds.length === 1 ? "" : "s"} staged`}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        title="Close"
                        style={{
                            width: 28, height: 28, borderRadius: 9, border: "1px solid var(--divider)",
                            background: "transparent", cursor: "pointer", display: "inline-flex",
                            alignItems: "center", justifyContent: "center", flex: "0 0 auto"
                        }}
                    >
                        <NPIcon name="x" size={12} sw={2.2} color="var(--fg3)" />
                    </button>
                </div>

                {fileIds.length === 0 ? (
                    <div style={{ fontSize: 12.5, color: "var(--fg3)", marginBottom: 14 }}>
                        No files staged yet. Upload specs, personas, or design docs — your own documents
                        become ground truth the AI can quote.
                    </div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
                        {fileIds.map(id => (
                            <div
                                key={id}
                                style={{
                                    display: "flex", alignItems: "center", gap: 10,
                                    padding: "9px 12px", borderRadius: 11, background: "var(--bg-input)"
                                }}
                            >
                                <NPGlyph name="files" size={15} />
                                <span style={{
                                    flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600,
                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
                                }}>
                                    {nameFor(id)}
                                </span>
                                <button
                                    type="button"
                                    aria-label={`Remove ${nameFor(id)}`}
                                    disabled={removingId === id}
                                    onClick={() => handleRemove(id)}
                                    style={{
                                        width: 24, height: 24, borderRadius: 8, border: "1px solid var(--divider)",
                                        background: "transparent", display: "inline-flex", alignItems: "center",
                                        justifyContent: "center", flex: "0 0 auto",
                                        cursor: removingId === id ? "default" : "pointer",
                                        opacity: removingId === id ? 0.55 : 1
                                    }}
                                >
                                    <NPIcon name="x" size={10} sw={2.2} color="var(--fg3)" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div
                    onDragOver={event => {
                        event.preventDefault();
                        setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={event => {
                        event.preventDefault();
                        setDragOver(false);
                        const dropped = event.dataTransfer.files;
                        if (dropped && dropped.length > 0) queue.enqueueFiles(Array.from(dropped));
                    }}
                    onClick={() => inputRef.current?.click()}
                    style={{
                        marginTop: 8,
                        border: `1px dashed ${dragOver ? "var(--np-brand)" : "var(--divider)"}`,
                        borderRadius: 11,
                        background: dragOver ? "rgba(54, 72, 208, 0.07)" : "var(--bg-input)",
                        padding: "13px 12px",
                        cursor: "pointer",
                        transition: "background 150ms ease, border-color 150ms ease"
                    }}
                >
                    <input
                        ref={inputRef}
                        type="file"
                        hidden
                        multiple
                        accept={ACCEPTED_FILE_TYPES}
                        onChange={handlePick}
                        data-testid="np-files-input"
                    />
                    <div style={{ fontSize: 12.5, fontWeight: 650, color: "var(--fg2)" }}>
                        Drop files here or click to choose
                    </div>
                    <div style={{ fontSize: 11.5, color: "var(--fg4)", marginTop: 4 }}>
                        Multi-select enabled. Uploads continue even if one file fails.
                    </div>
                </div>

                <UploadQueueList rows={uploadRows} onRemove={queue.removeQueuedRow} onRetry={queue.retryRow} />

                {recentlyIndexedRows.length > 0 && (
                    <div style={{ marginTop: 12, fontSize: 11.5, color: "var(--fg4)" }}>
                        Uploaded: {recentlyIndexedRows.map(row => row.name).join(", ")}
                    </div>
                )}

                <div style={{ fontSize: 11.5, color: "var(--fg4)", marginTop: 10 }}>
                    Files stay in your library and link to the product when it is created.
                </div>
            </div>
        </div>
    );
}
